import { jsx as _jsx } from "react/jsx-runtime";
import { batch, useComputed, useSignal, useSignalEffect } from "@preact/signals";
import isEqual from "@tinkoff/utils/is/equal";
import useFilterQueryParam from "../../../lib/hooks/use-filter-query-param";
import FilterSet from "../FilterSet";
import Button from "../../Button";
import { mapCollection } from "../../../lib/filter/filter-collection";
import useMessageChannel from "../../../lib/hooks/use-message-channel";
import { EVENT_FILTERS_UPDATED } from "../../../lib/hooks/use-on-filters-ready";
import ErrorBoundary from "../../ErrorBoundary";
import asArray from "../../../lib/as-array";
const QUERY_STRING_PARAM = "filters";
const DEFAULT_LOCAL_STORAGE_KEY = "global-filters";
export default function FilterSetWithApplyButton(props) {
    return (_jsx(ErrorBoundary, { onError: () => _jsx("p", { children: "Filters are not available" }), children: _jsx(InnerFilterSetWithApplyButton, Object.assign({}, props)) }));
}
/**
 * For an efficient and correct comparison of selected filter values, we ensure the selected value
 * is an array. Failure to do this can cause incorrect behaviour.
 */
function prepareFiltersForComparison(filters) {
    return mapCollection(filters, (filter) => asArray(filter.selected));
}
function InnerFilterSetWithApplyButton(props) {
    const { config, localStorageKey = DEFAULT_LOCAL_STORAGE_KEY } = props;
    const [filters, setFilters] = useFilterQueryParam(QUERY_STRING_PARAM, config, localStorageKey);
    const stagedFilters = useSignal(filters.peek());
    const channel = useMessageChannel();
    // Whenever the committed filters are changed -probably by a back/forward browser navigation- we
    // sync up the staged filters. This discards the staged filters (which is expected when making
    // these actions) and prevents the "apply" button from showing when it shouldn't.
    useSignalEffect(() => {
        batch(() => {
            var _a;
            stagedFilters.value = filters.value;
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, filters.value);
            channel.send(EVENT_FILTERS_UPDATED, true);
        });
    });
    const displayApplyButton = useComputed(() => {
        const selectedStagedFilters = prepareFiltersForComparison(stagedFilters.value);
        const selectedFilters = prepareFiltersForComparison(filters.value);
        return !isEqual(selectedStagedFilters, selectedFilters);
    });
    const commitFilters = () => {
        var _a;
        setFilters(stagedFilters.peek());
        (_a = props.onFiltersApplied) === null || _a === void 0 ? void 0 : _a.call(props, stagedFilters.peek());
    };
    const onFilterChange = (newFilters) => {
        stagedFilters.value = newFilters;
    };
    return (_jsx("div", { children: _jsx(FilterSet, Object.assign({}, props, { selected: filters.value, onChange: onFilterChange, children: displayApplyButton.value ? (_jsx(Button, { variation: "primary", onClick: commitFilters, children: "Apply" })) : null })) }));
}
